"use client";

import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "@/lib/reduced-motion";

/**
 * Salida animada sin librerías (sustituto de AnimatePresence para un
 * fade/scale de 150ms). Al pasar `open` a false, mantiene montado el
 * componente durante `durationMs` con `isLeaving=true` para que corra la
 * clase `animate-out`; luego desmonta. Reapertura a mitad de salida la
 * cancela. Con `prefers-reduced-motion` no hay espera: desmonta directo.
 */
export function useAnimatedOpen(
  open: boolean,
  durationMs = 150,
): { shouldRender: boolean; isLeaving: boolean } {
  const [shouldRender, setShouldRender] = useState(open);
  const [isLeaving, setIsLeaving] = useState(false);
  // Se lee/escribe solo dentro de effects (nunca en render): indica si el
  // contenido sigue montado para decidir salida animada vs desmontaje directo.
  const renderRef = useRef(open);
  const timer = useRef<number | null>(null);

  // Sincroniza el ref fuera del render (react-hooks/refs lo exige).
  useEffect(() => {
    renderRef.current = shouldRender;
  }, [shouldRender]);

  // Limpieza al desmontar el hook.
  useEffect(() => {
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // 2026-09-26 FASE 6 COMPETENCIA oferta 8/9 T2: chequeo vía SSOT
    // prefersReducedMotion (semántica idéntica, sin cambio visual).
    const reduced = prefersReducedMotion();
    if (open) {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      // Sync intencional: open externo -> montado inmediato, cancela salida a mitad de animación.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync intencional open->montado con salida cancelable
      setShouldRender(true);
      setIsLeaving(false);
      return;
    }
    if (reduced || !renderRef.current) {
      // Sin animación (reduced-motion o ya desmontado): desmonta directo.
      setShouldRender(false);
      setIsLeaving(false);
      return;
    }
    // Inicia salida animada; el timeout desmonta tras durationMs.
    setIsLeaving(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setShouldRender(false);
      setIsLeaving(false);
      timer.current = null;
    }, durationMs);
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [open, durationMs]);

  return { shouldRender, isLeaving };
}
